import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const testimonialsRes = await axios.get(`${API}/testimonials`);
      setTestimonials(testimonialsRes.data);
      console.log('Fetched testimonials:', testimonialsRes.data.length);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <section className="relative min-h-[500px] h-[600px] md:h-[700px] overflow-hidden teal-gradient-bg" data-testid="hero-section">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1603943540834-83cafbdb5a92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGZhbWlseSUyMHdlYXJpbmclMjBib290cyUyMGF1dHVtbiUyMG91dGRvb3JzfGVufDB8fHx8MTc3MjA1NDQ4NHww&ixlib=rb-4.1.0&q=85"
            alt="Family walking in autumn boots"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative h-full flex items-center justify-center text-center px-4 md:px-6 pt-8 pb-32 md:pb-24">
          <div className="max-w-4xl w-full">
            <div className="inline-block px-3 md:px-4 py-1.5 md:py-2 bg-[#5EEAD4]/20 rounded-full mb-4 md:mb-6 border border-[#5EEAD4]/30">
              <p className="text-[#5EEAD4] font-medium text-xs md:text-sm">Premium Footwear for Your Family</p>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold font-heading text-white mb-4 md:mb-6 px-2" data-testid="hero-title">
              Soft, Comfy, Unique Shoes
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-6 md:mb-8 font-medium px-2" data-testid="hero-subtitle">
              For the whole family that will keep you moving!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-2">
              <Link to="/shop" data-testid="hero-shop-button">
                <button className="btn-primary inline-flex items-center justify-center group w-full sm:w-auto">
                  Shop Now
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <Link to="/team" data-testid="hero-team-button">
                <button className="btn-outline bg-white/10 backdrop-blur-md text-white border-white hover:bg-white hover:text-[#0C4A6E] w-full sm:w-auto">
                  Meet Our Team
                </button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 w-full max-w-5xl px-4 md:px-6">
          <div className="grid grid-cols-3 md:grid-cols-3 gap-2 md:gap-4">
            <div className="stat-card text-center py-3 md:py-4">
              <p className="text-xl md:text-3xl font-bold text-[#5EEAD4] mb-0.5 md:mb-1">500+</p>
              <p className="text-white/80 text-xs md:text-sm">Happy Customers</p>
            </div>
            <div className="stat-card text-center py-3 md:py-4">
              <p className="text-xl md:text-3xl font-bold text-[#5EEAD4] mb-0.5 md:mb-1">6</p>
              <p className="text-white/80 text-xs md:text-sm">Premium Brands</p>
            </div>
            <div className="stat-card text-center py-3 md:py-4">
              <p className="text-xl md:text-3xl font-bold text-[#5EEAD4] mb-0.5 md:mb-1">99%</p>
              <p className="text-white/80 text-xs md:text-sm">Satisfaction Rate</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-12 xl:px-24 bg-white" data-testid="about-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-xs md:text-sm uppercase tracking-wider text-[#14B8A6] mb-2 md:mb-3 font-semibold">
              About Us
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-4 px-2">
              About Family Shoe Store
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="space-y-6">
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-gray-900">
                Serving Corbin, KY for Over 25 Years
              </h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Family Shoe Store has been a cornerstone of the Corbin community since our founding. We're proud to offer soft, comfy, and unique shoes for the whole family that will keep you moving!
              </p>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Founded by Leopold Irungu, our store has grown from a small family business into a trusted destination for quality footwear. With over 70 dedicated staff members, we're committed to providing exceptional service and helping every customer find their perfect fit.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="bg-gradient-to-br from-[#14B8A6] to-[#0D9488] text-white p-6 rounded-xl text-center">
                  <p className="text-3xl font-bold mb-1">25+</p>
                  <p className="text-sm font-medium">Years in Business</p>
                </div>
                <div className="bg-gradient-to-br from-[#14B8A6] to-[#0D9488] text-white p-6 rounded-xl text-center">
                  <p className="text-3xl font-bold mb-1">70+</p>
                  <p className="text-sm font-medium">Team Members</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 p-6 md:p-8 rounded-xl border-2 border-gray-200">
                <h4 className="text-xl font-heading font-bold text-gray-900 mb-4">Our Mission</h4>
                <p className="text-gray-700 leading-relaxed">
                  To provide quality footwear and exceptional service to families in Corbin and beyond. We believe that the right shoes can make all the difference in comfort and confidence.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-teal-50 p-6 md:p-8 rounded-xl border-2 border-[#14B8A6]">
                <h4 className="text-xl font-heading font-bold text-gray-900 mb-4">Why Choose Us?</h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-[#14B8A6] mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Premium brands including Timberland, Dr. Martens, Ariat, and more</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-[#14B8A6] mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Expert staff ready to help you find the perfect fit</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-[#14B8A6] mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Family-owned and operated with 25+ years of experience</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/team" data-testid="meet-team-link">
              <button className="btn-primary">Meet Our Team</button>
            </Link>
          </div>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 xl:px-24">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-xs md:text-sm uppercase tracking-wider text-[#14B8A6] mb-2 md:mb-3 font-semibold">Happy Customers</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-3 md:mb-4" data-testid="testimonials-heading">
                What Our Customers Say
              </h2>
              <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
                Real reviews from our amazing customers
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-testid="testimonials-grid">
              {testimonials.slice(0, 3).map(testimonial => (
                <div
                  key={testimonial.id}
                  className="bg-white p-6 md:p-8 professional-shadow-lg rounded-xl hover-lift border-2 border-gray-200"
                  data-testid={`testimonial-card-${testimonial.id}`}
                >
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#14B8A6] text-[#14B8A6]" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed text-base" data-testid="testimonial-message">
                    "{testimonial.message}"
                  </p>
                  <p className="font-bold text-gray-900 text-lg" data-testid="testimonial-name">
                    {testimonial.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-24 teal-gradient-bg text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center px-4 md:px-6">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 md:mb-6 px-2" data-testid="cta-heading">
            Ready to Find Your Perfect Fit?
          </h2>
          <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8 px-2" data-testid="cta-description">
            Visit our store or shop online today. We have 70 staff members ready to help you!
          </p>
          <Link to="/shop" data-testid="cta-button">
            <button className="bg-white text-[#0C4A6E] px-6 md:px-8 py-3 md:py-4 text-sm md:text-base font-semibold rounded-lg hover:bg-gray-50 transition-all shadow-lg">
              Start Shopping
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;